import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

const stripePromise = loadStripe('pk_live_51MeuHnBq44xeUkkuos2fn9jP9LKxs6qoKtfLPvAFNXwBmrGcdwd4LlvSUTLcz4rqu5znMdgTN1mdqa8cG0367LgG00KkKSwqkj');

export default function PaymentPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const { subscriptionStatus, updateSubscriptionStatus } = useSubscriptionStatus();

  const plans = [
    {
      id: 'premium',
      name: 'Premium Monthly',
      price: 9.99,
      originalPrice: 19.99,
      period: 'per month',
      features: [
        'Unlimited messaging with all members',
        'Advanced search & filtering',
        'Join unlimited travel groups',
        'Create local meetups',
        'Priority customer support',
        'Personality compatibility scores',
        'Travel safety verification',
        'Exclusive travel deals'
      ],
      popular: true,
      savings: '50% off'
    },
    {
      id: 'annual',
      name: 'Annual Plan',
      price: 99,
      originalPrice: 239.88,
      period: 'per year',
      features: [
        'Everything in Premium',
        '2 months completely free',
        'Exclusive travel events access',
        'VIP customer support',
        'Early access to new features',
        'Custom travel planning service',
        'Group booking discounts',
        'Travel insurance included'
      ],
      popular: false,
      savings: 'Save £140.88'
    }
  ];

  const handleSubscribe = async (planId) => {
    try {
      setLoading(true);
      setError('');

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Create checkout session
      const response = await fetch('/api/stripe-checkout/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: planId,
          price: planId === 'premium' ? 9.99 : 99,
          currency: 'gbp',
          feature: 'premium-subscription'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment session');
      }

      const { sessionId } = await response.json();

      // Redirect to Stripe checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanData = plans.find(plan => plan.id === selectedPlan);

  return (
    <div className="payment-page" style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 'var(--spacing-8) 0'
    }}>
      <div className="container">
        <div className="row justify-center">
          <div className="col col--10">
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-8)', color: 'white' }}>
              <div style={{ fontSize: '3em', marginBottom: 'var(--spacing-4)' }}>🚀</div>
              <h1 style={{ 
                fontSize: '2.5em', 
                marginBottom: 'var(--spacing-3)',
                fontWeight: 'bold'
              }}>
                Unlock Your Travel Adventure
              </h1>
              <p style={{ 
                fontSize: '1.3em', 
                opacity: 0.9,
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Join thousands of travelers who have found their perfect companions
              </p>
            </div>

            {error && (
              <div style={{
                background: '#ef4444',
                color: 'white',
                padding: 'var(--spacing-4)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--spacing-6)',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            {/* Plans Grid */}
            <div className="row" style={{ gap: 'var(--spacing-6)', marginBottom: 'var(--spacing-8)' }}>
              {plans.map((plan) => (
                <div key={plan.id} className="col col--6">
                  <div className="plan-card" style={{
                    background: 'white',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--spacing-6)',
                    height: '100%',
                    position: 'relative',
                    border: plan.popular ? '3px solid #4ade80' : '1px solid #e5e7eb',
                    transform: plan.popular ? 'scale(1.02)' : 'none',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                  }}>
                    {plan.popular && (
                      <div style={{
                        position: 'absolute',
                        top: '-15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#4ade80',
                        color: 'white',
                        padding: 'var(--spacing-2) var(--spacing-4)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'bold'
                      }}>
                        MOST POPULAR
                      </div>
                    )}

                    <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-6)' }}>
                      <h3 style={{ 
                        fontSize: '1.5em', 
                        marginBottom: 'var(--spacing-3)',
                        color: '#1f2937'
                      }}>
                        {plan.name}
                      </h3>
                      
                      <div style={{ marginBottom: 'var(--spacing-2)' }}>
                        <span style={{ 
                          fontSize: '3em', 
                          fontWeight: 'bold',
                          color: '#4ade80'
                        }}>
                          £{plan.price}
                        </span>
                        <span style={{ 
                          color: '#6b7280',
                          fontSize: '1.2em'
                        }}>
                          /{plan.period}
                        </span>
                      </div>

                      {plan.originalPrice && (
                        <div style={{ marginBottom: 'var(--spacing-3)' }}>
                          <span style={{ 
                            textDecoration: 'line-through',
                            color: '#9ca3af',
                            fontSize: '1.1em'
                          }}>
                            £{plan.originalPrice}
                          </span>
                          <span style={{ 
                            color: '#4ade80',
                            fontWeight: 'bold',
                            marginLeft: 'var(--spacing-2)'
                          }}>
                            {plan.savings}
                          </span>
                        </div>
                      )}
                    </div>

                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      marginBottom: 'var(--spacing-6)'
                    }}>
                      {plan.features.map((feature, index) => (
                        <li key={index} style={{ 
                          marginBottom: 'var(--spacing-3)',
                          paddingLeft: 'var(--spacing-4)',
                          position: 'relative',
                          color: '#374151'
                        }}>
                          <span style={{ 
                            position: 'absolute', 
                            left: 0, 
                            top: '2px',
                            color: '#4ade80',
                            fontSize: '1.2em'
                          }}>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={loading}
                      style={{
                        width: '100%',
                        background: plan.popular ? '#4ade80' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: 'var(--spacing-4)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {loading ? 'Processing...' : 'Start Free Trial'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Section */}
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              padding: 'var(--spacing-6)', 
              borderRadius: 'var(--radius-lg)',
              color: 'white',
              textAlign: 'center',
              marginBottom: 'var(--spacing-6)'
            }}>
              <h3 style={{ marginBottom: 'var(--spacing-4)' }}>Why Choose GradedWash Premium?</h3>
              
              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--4">
                  <div style={{ fontSize: '2em', marginBottom: 'var(--spacing-2)' }}>🛡️</div>
                  <h4>Safe & Verified</h4>
                  <p style={{ opacity: 0.9 }}>All members are verified and safety-checked</p>
                </div>
                <div className="col col--4">
                  <div style={{ fontSize: '2em', marginBottom: 'var(--spacing-2)' }}>🧠</div>
                  <h4>Smart Matching</h4>
                  <p style={{ opacity: 0.9 }}>AI-powered personality compatibility</p>
                </div>
                <div className="col col--4">
                  <div style={{ fontSize: '2em', marginBottom: 'var(--spacing-2)' }}>🌍</div>
                  <h4>Global Community</h4>
                  <p style={{ opacity: 0.9 }}>Connect with travelers worldwide</p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div style={{ 
              background: 'white', 
              padding: 'var(--spacing-6)', 
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--spacing-6)'
            }}>
              <h3 style={{ 
                textAlign: 'center', 
                marginBottom: 'var(--spacing-6)',
                color: '#1f2937'
              }}>
                Frequently Asked Questions
              </h3>
              
              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                <div className="col col--6">
                  <h4 style={{ color: '#374151', marginBottom: 'var(--spacing-2)' }}>Can I cancel anytime?</h4>
                  <p style={{ color: '#6b7280' }}>Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
                </div>
                <div className="col col--6">
                  <h4 style={{ color: '#374151', marginBottom: 'var(--spacing-2)' }}>Is there a free trial?</h4>
                  <p style={{ color: '#6b7280' }}>Yes! Premium plans come with a 7-day free trial. No credit card required to start.</p>
                </div>
                <div className="col col--6">
                  <h4 style={{ color: '#374151', marginBottom: 'var(--spacing-2)' }}>What payment methods do you accept?</h4>
                  <p style={{ color: '#6b7280' }}>We accept all major credit cards, debit cards, and PayPal. All payments are processed securely through Stripe.</p>
                </div>
                <div className="col col--6">
                  <h4 style={{ color: '#374151', marginBottom: 'var(--spacing-2)' }}>Can I upgrade or downgrade?</h4>
                  <p style={{ color: '#6b7280' }}>Absolutely! You can change your plan at any time. Changes take effect immediately.</p>
                </div>
              </div>
            </div>

            {/* Back to Pricing */}
            <div style={{ textAlign: 'center' }}>
              <Link to="/pricing" style={{
                color: 'white',
                textDecoration: 'none',
                fontSize: '1.1em',
                opacity: 0.9
              }}>
                ← Back to Pricing Plans
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 